import { MapNumberTo, MapStringTo, Nullable, Object_Unknown } from "../../../base-types";
import { IContentHubClient } from "../../../clients/content-hub-client";
import DirtyValueCalculator from "../../../dirty-value-calculator";
import Guard from "../../../guard";
import { EntityLoadConfiguration } from "../../querying/entity-load-configuration";
import { IParentRelation, RelationBase } from "../relation";
import { RelationRole } from "../relation-role";
import { IToOneRelation } from "./to-one-relation";

/**
 * A relation where the parent has one child.
 */
export interface IParentToOneChildRelation extends IParentRelation, IToOneRelation {
    /**
     * The child id.
     */
    child: Nullable<number>;

    /**
     * Gets the properties for a child.
     *
     * If no properties are found, an empty list is returned.
     */
    getChildProperties(): MapStringTo<Object_Unknown>;
}

export class ParentToOneChildRelation extends RelationBase implements IParentToOneChildRelation {
    private _child: Nullable<number> = null;

    readonly role: RelationRole = RelationRole.Parent;
    readonly isMultiValue: boolean = false;

    get child(): Nullable<number> {
        return this._child;
    }

    set child(id: Nullable<number>) {
        Guard.validIdOrNull(id);
        this._child = id;
    }

    get isDirty(): boolean {
        if (!this.isTracking) {
            return false;
        } else {
            return this._dirtyValueCalculator.isDirty(this._child);
        }
    }

    constructor(
        name: string,
        properties: Nullable<MapNumberTo<MapStringTo<Object_Unknown>>> = null,
        client?: IContentHubClient
    ) {
        super(name, properties, client);
    }

    getIds(): Array<number> {
        if (this._child !== 0 && this._child !== null) {
            return [this._child];
        } else {
            return [];
        }
    }

    setIds(ids: Array<number>): void {
        Guard.arrayMaxOne(ids);
        if (ids.length > 0) {
            Guard.validId(ids[0]);
            this._child = ids[0];
        } else {
            this._child = null;
        }
    }

    async setIdentifierAsync(identifier: Nullable<string>): Promise<void> {
        Guard.notNullOrUndefined(this.client);
        Guard.stringNotNullOrEmpty(identifier);

        if (identifier) {
            const entity = await this.client!.entities.getAsync(identifier, EntityLoadConfiguration.Minimal);

            if (entity && entity.id) {
                this.setId(entity.id);
                return;
            }
        }

        this.setId(null);
    }

    clear(): void {
        this._child = null;
    }

    getId(): Nullable<number> {
        return this._child;
    }

    setId(id: Nullable<number>): void {
        Guard.validIdOrNull(id);
        this._child = id;
    }

    startTracking(): void {
        if (this.isTracking) return;

        this.isTracking = true;

        this._dirtyValueCalculator = new DirtyValueCalculator();
        this._dirtyValueCalculator.setOriginalValue(this._child);
    }

    markClean(): void {
        if (this.isTracking) {
            this._dirtyValueCalculator.setOriginalValue(this._child);
        }
    }

    getChildProperties(): MapStringTo<Object_Unknown> {
        if (this._child == null) {
            return {};
        }

        return this.properties[this._child] == null ? {} : this.properties[this._child];
    }
}
